import {
  mapMeetingSurfaceCardToMeetingSummary,
  mapMeetingSummaryToHomeMeeting,
} from "@/components/agentic-surface/adapters";
import { OPENWORK_ADVISOR_SURFACE_ID } from "@/components/home/resolveHomeSurfaceId";
import {
  buildOpenworkStandupBrief,
  type OpenworkNextMeetingBrief,
} from "@/components/home/standup/openworkStandupBrief";
import { normalizeOpenworkLetterReviewQueue } from "@/components/review/reviewDemoHelpers";
import { getSurfaceSnapshot } from "@/services/api/agentic-v2";
import {
  buildOpenworkInAppAgentContext,
  type InAppAgentRuntimeContext,
} from "@/services/inAppAgent/buildInAppAgentRuntimeContext";
import { resolveAdviserPersonaName } from "@/utils/adviserPersonaName";

/** Pull the same Home snapshot Ani should trust — used right before each sidebar send. */
export async function fetchOpenworkInAppAgentContext(params: {
  companyId: string;
  personaName: string;
  assigneeUserId?: string;
  assigneeEmail?: string | null;
}): Promise<InAppAgentRuntimeContext> {
  const snapshot = await getSurfaceSnapshot(OPENWORK_ADVISOR_SURFACE_ID, {
    visibility: "me",
    platform: "web",
    assignee_user_id: params.assigneeUserId,
  });

  const reviewTasks = normalizeOpenworkLetterReviewQueue(snapshot.task_buckets?.needs_me ?? [], {
    assigneeEmail: params.assigneeEmail,
  });
  const meetings = [...(snapshot.meetings ?? [])].sort((a, b) => {
    const left = a.scheduled_at ? new Date(a.scheduled_at).getTime() : Number.POSITIVE_INFINITY;
    const right = b.scheduled_at ? new Date(b.scheduled_at).getTime() : Number.POSITIVE_INFINITY;
    return left - right;
  });
  const nextMeetingSummary = meetings[0]
    ? mapMeetingSurfaceCardToMeetingSummary(meetings[0])
    : null;
  const nextMeeting: OpenworkNextMeetingBrief | null = nextMeetingSummary
    ? (() => {
        const home = mapMeetingSummaryToHomeMeeting(nextMeetingSummary);
        return {
          clientName: home.clientName,
          title: home.title ?? "Client meeting",
          dateLabel: home.dateLabel,
          time: home.time,
        };
      })()
    : null;

  const briefing = buildOpenworkStandupBrief(params.personaName, reviewTasks, false, {
    allLettersApproved: reviewTasks.length === 0,
    approvedCount: 0,
    nextMeeting,
  });

  return buildOpenworkInAppAgentContext({
    companyId: params.companyId,
    surfaceId: OPENWORK_ADVISOR_SURFACE_ID,
    briefing,
    reviewTasks,
    nextMeeting,
    assigneeEmail: params.assigneeEmail,
  });
}

export function resolveOpenworkPersonaName(user: {
  name?: string | null;
  first_name?: string | null;
  email?: string | null;
} | null | undefined): string {
  return resolveAdviserPersonaName(user ?? undefined);
}
